import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Table, Modal, Select, Button, message, Tabs, Tag, Tooltip,
  Spin, Empty, Statistic, Row, Col, Dropdown, Menu,
} from 'antd';
import {
  ExclamationCircleFilled, ClockCircleFilled, CheckCircleFilled,
  UserSwitchOutlined, PlayCircleOutlined, UndoOutlined, EyeOutlined,
  ReloadOutlined, GlobalOutlined, CheckOutlined, MoreOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  getTeleradiologyQueue,
  getTeleradiologyWorklist,
  assignTeleradiologyGroup,
  claimTeleradiologyRequest,
  unclaimTeleradiologyRequest,
  completeTeleradiologyRequest,
  cancelTeleradiologyRequest,
  rejectTeleradiologyRequest,
  getTeleradiologyHistory,
  getTeleradiologyStats,
  getTeleradiologyGroups,
} from './TeleradiologyService';
import { getReportForStudy } from '../studies/StudiesService';

const { TabPane } = Tabs;
const { Option } = Select;

/* ── Constants ────────────────────────────────────────────────────────────── */

const TELE_TENANT = 'teleradiology';

// Radiologist groups — add new groups here as the network grows.
// group_id values must match the Keycloak user attribute and routing rules.
const DEFAULT_GROUPS = [
  { id: 'group_a', name: 'Group A — General Radiology', color: '#2563eb' },
  { id: 'group_b', name: 'Group B — Neuroradiology / Specialist', color: '#7c3aed' },
  { id: 'group_c', name: 'Group C — On-Call / STAT', color: '#dc2626' },
];

const URGENCY_CONFIG = {
  STAT:    { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: <ExclamationCircleFilled />, label: 'STAT',    sla: '≤ 30 min' },
  URGENT:  { color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: <ClockCircleFilled />,       label: 'Urgent',  sla: '≤ 2 hrs'  },
  ROUTINE: { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: <CheckCircleFilled />,       label: 'Routine', sla: '≤ 24 hrs' },
};

const STATUS_CONFIG = {
  unassigned:    { color: 'default',  text: 'Unassigned'     },
  group_assigned: { color: 'blue',    text: 'Group Assigned'  },
  claimed:       { color: 'purple',   text: 'Claimed'         },
  completed:     { color: 'success',  text: 'Completed'       },
  cancelled:     { color: 'error',    text: 'Cancelled'       },
};

/* ── Small helpers ────────────────────────────────────────────────────────── */

/* ── SLA limits per urgency (minutes) ──────────────────────────────────────── */
const SLA_LIMITS = { STAT: 30, URGENT: 120, ROUTINE: 1440 };

/* Elapsed time display with SLA-aware colour coding.
   Re-renders via the page-level `tick` state so all cells update together. */
const TATCell = ({ submittedAt, urgency }) => {
  if (!submittedAt) return <span style={{ color: '#9ca3af' }}>—</span>;
  const elapsedMin = (Date.now() - new Date(submittedAt)) / 60000;
  const limitMin   = SLA_LIMITS[urgency] || SLA_LIMITS.ROUTINE;
  const pct        = (elapsedMin / limitMin) * 100;

  let color;
  if (pct >= 100) color = '#dc2626';       // overdue — red
  else if (pct >= 80) color = '#d97706';   // approaching — amber
  else if (pct >= 50) color = '#f59e0b';   // halfway — yellow
  else                color = '#16a34a';   // comfortable — green

  const fmt = (m) => {
    if (m < 60) return `${Math.round(m)}m`;
    const h = Math.floor(m / 60), rem = Math.round(m % 60);
    return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
  };

  return (
    <Tooltip title={`SLA: ${fmt(limitMin)} · ${Math.round(pct)}% used`}>
      <span style={{ color, fontWeight: 600, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {fmt(elapsedMin)}
        {pct >= 100 && <ExclamationCircleFilled style={{ fontSize: 11 }} />}
      </span>
    </Tooltip>
  );
};

const UrgencyBadge = ({ urgency }) => {
  const c = URGENCY_CONFIG[urgency] || { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', icon: null, label: urgency || '—' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700,
      color: c.color, background: c.bg, border: `1px solid ${c.border}`,
    }}>
      {c.icon} {c.label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const c = STATUS_CONFIG[status] || { color: 'default', text: status };
  return <Tag color={c.color} style={{ fontSize: 12, margin: 0 }}>{c.text}</Tag>;
};

const formatTs = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const openViewer = (studyInstanceUID) => {
  if (!studyInstanceUID) { message.warning('Study UID not available'); return; }
  window.open('/afya-viewer/viewer?StudyInstanceUIDs=' + encodeURIComponent(studyInstanceUID), '_blank');
};

/* ── Stats card ───────────────────────────────────────────────────────────── */

const StatsCard = ({ label, value, color }) => (
  <div style={{
    background: '#fff', borderRadius: 10, padding: '16px 20px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,.04)',
  }}>
    <Statistic
      title={<span style={{ fontSize: 13, color: '#6b7280' }}>{label}</span>}
      value={value}
      valueStyle={{ fontSize: 28, fontWeight: 700, color: color || '#111827' }}
    />
  </div>
);

/* ── Main component ───────────────────────────────────────────────────────── */

const TeleradiologyPage = ({ profile }) => {
  const roles       = profile?.realm_roles || [];
  const hospitalId  = profile?.hospital_id || '';
  const groupId     = profile?.group_id    || '';
  const userId      = profile?.sub || profile?.id || '';
  const username    = profile?.preferred_username || '';

  const isTeleUser      = hospitalId === TELE_TENANT;
  const isCoordinator   = isTeleUser && roles.some(r => ['PO', 'PROJECT_OWNER', 'MANAGER', 'ADMIN'].includes(r));
  const isRadiologist   = isTeleUser && !isCoordinator;

  /* ── Coordinator state ── */
  const [queue, setQueue]               = useState([]);
  const [queueTotal, setQueueTotal]     = useState(0);
  const [queueLoading, setQueueLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [queuePage, setQueuePage]       = useState(1);
  const PAGE_SIZE = 10;

  const [assignModal, setAssignModal]   = useState({ open: false, request: null });
  const [selectedGroup, setSelectedGroup] = useState('');
  const [assigning, setAssigning]       = useState(false);

  /* ── Radiologist state ── */
  const [worklist, setWorklist]         = useState([]);
  const [worklistTotal, setWorklistTotal] = useState(0);
  const [worklistLoading, setWorklistLoading] = useState(false);
  const [worklistTab, setWorklistTab]   = useState('group');
  const [history, setHistory]           = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [stats, setStats]               = useState(null);
  const [groups, setGroups]             = useState(DEFAULT_GROUPS);
  const [worklistPage, setWorklistPage] = useState(1);
  const [claimingId, setClaimingId]     = useState(null);
  const [cancelingId, setCancelingId]   = useState(null);
  const [rejectModal, setRejectModal]   = useState({ open: false, requestId: null, patientName: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting]       = useState(false);
  // Tick every 30 s so all TATCell components re-render with fresh elapsed times.
  const [, setTick]                     = useState(0);
  // reportStatusCache: { [studyInstanceUID]: 'none' | 'draft' | 'submitted' }
  const [reportStatusCache, setReportStatusCache] = useState({});

  /* ── Counts for stats cards ── */
  const [counts, setCounts] = useState({ unassigned: 0, group_assigned: 0, claimed: 0, completed: 0 });

  /* ── Data loaders ── */

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const { data } = await getTeleradiologyQueue({
        status: statusFilter, urgency: urgencyFilter,
        from: (queuePage - 1) * PAGE_SIZE, size: PAGE_SIZE,
      });
      setQueue(data?.data || []);
      setQueueTotal(data?.total || 0);
    } catch (e) {
      message.error('Failed to load queue');
    } finally {
      setQueueLoading(false);
    }
  }, [statusFilter, urgencyFilter, queuePage]);

  const loadCounts = useCallback(async () => {
    try {
      const statuses = ['unassigned', 'group_assigned', 'claimed', 'completed'];
      const results = await Promise.all(
        statuses.map(s => getTeleradiologyQueue({ status: s, size: 1 }).catch(() => ({ data: { total: 0 } })))
      );
      const next = {};
      statuses.forEach((s, i) => { next[s] = results[i]?.data?.total || 0; });
      setCounts(next);
    } catch (_) {}
  }, []);

  const loadWorklist = useCallback(async () => {
    setWorklistLoading(true);
    try {
      const { data } = await getTeleradiologyWorklist({
        from: (worklistPage - 1) * PAGE_SIZE, size: PAGE_SIZE,
      });
      setWorklist(data?.data || []);
      setWorklistTotal(data?.total || 0);
    } catch (e) {
      message.error('Failed to load worklist');
    } finally {
      setWorklistLoading(false);
    }
  }, [worklistPage]);

  useEffect(() => {
    if (isCoordinator) {
      loadQueue();
      loadCounts();
      getTeleradiologyStats().then(({ data }) => setStats(data)).catch(() => {});
    }
    if (isRadiologist) { loadWorklist(); }
  }, [isCoordinator, isRadiologist, loadQueue, loadWorklist, loadCounts]); // eslint-disable-line

  // Load history: on mount AND whenever the radiologist switches to the history
  // tab OR completes a study (worklistTab changes to 'history', or we call
  // loadHistory() directly after handleComplete).
  const loadHistory = useCallback(() => {
    if (!isRadiologist) return;
    setHistoryLoading(true);
    getTeleradiologyHistory({ size: 200 })
      .then(({ data }) => {
        setHistory(data?.data || []);
        setHistoryTotal(data?.total || 0);
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [isRadiologist]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Re-fetch history when the user switches to the history tab.
  useEffect(() => {
    if (isRadiologist && worklistTab === 'history') loadHistory();
  }, [worklistTab]); // eslint-disable-line

  // Fetch (or re-fetch) report status for every claimed study each time
  // the worklist refreshes or the user switches to the "My Studies" tab.
  // No cache guard — the radiologist may have submitted a report in the
  // viewer between the last load and coming back to this tab.
  useEffect(() => {
    if (!isRadiologist) return;
    const claimed = worklist.filter(r => r.status === 'claimed' && r.study_instance_uid);
    if (claimed.length === 0) return;
    claimed.forEach(r => {
      getReportForStudy(r.study_instance_uid)
        .then(res => {
          const status = res?.data?.status || 'none';
          setReportStatusCache(prev => ({ ...prev, [r.study_instance_uid]: status }));
        })
        .catch(() => {
          setReportStatusCache(prev => ({ ...prev, [r.study_instance_uid]: 'none' }));
        });
    });
  }, [worklist, isRadiologist, worklistTab]); // re-runs when tab switches to 'mine'

  // Auto-refresh counts every 30 seconds so coordinator sees completions
  // made by radiologists without a manual refresh.
  useEffect(() => {
    if (!isCoordinator) return;
    const interval = setInterval(() => { loadCounts(); loadQueue(); }, 30000);
    return () => clearInterval(interval);
  }, [isCoordinator, loadCounts, loadQueue]);

  // Load group definitions from API (falls back to DEFAULT_GROUPS on error).
  useEffect(() => {
    if (!isTeleUser) return;
    getTeleradiologyGroups()
      .then(({ data }) => { if (data?.data?.length) setGroups(data.data); })
      .catch(() => {});
  }, [isTeleUser]); // eslint-disable-line

  // Tick every 30 s to keep TATCell elapsed times current without a full re-fetch.
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  /* ── Coordinator: assign to group ── */

  const handleAssignGroup = async () => {
    if (!selectedGroup) { message.warning('Select a group'); return; }
    setAssigning(true);
    try {
      await assignTeleradiologyGroup(assignModal.request.id, selectedGroup);
      message.success(`Study assigned to ${groups.find(g => g.id === selectedGroup)?.name || selectedGroup}`);
      setAssignModal({ open: false, request: null });
      setSelectedGroup('');
      loadQueue();
      loadCounts();
    } catch (e) {
      const err = e.response?.data?.error || 'Assignment failed';
      message.error(err);
    } finally {
      setAssigning(false);
    }
  };

  /* ── Radiologist: claim / unclaim ── */

  const handleClaim = async (requestId) => {
    setClaimingId(requestId);
    try {
      await claimTeleradiologyRequest(requestId);
      message.success('Study claimed — it is now in your worklist');
      loadWorklist();
    } catch (e) {
      const err = e.response?.data?.error || 'Could not claim study';
      message.error(err);
    } finally {
      setClaimingId(null);
    }
  };

  const handleUnclaim = async (requestId) => {
    setClaimingId(requestId);
    try {
      await unclaimTeleradiologyRequest(requestId);
      message.success('Study released back to group queue');
      loadWorklist();
    } catch (e) {
      message.error('Could not release study');
    } finally {
      setClaimingId(null);
    }
  };

  const handleCancel = async (requestId, patientName) => {
    Modal.confirm({
      title: 'Cancel teleradiology request?',
      content: `This will withdraw "${patientName || 'this study'}" from the reading queue. The action cannot be undone for completed studies.`,
      okText: 'Cancel Request',
      okType: 'danger',
      cancelText: 'Keep',
      onOk: async () => {
        setCancelingId(requestId);
        try {
          await cancelTeleradiologyRequest(requestId);
          message.success('Request cancelled');
          loadQueue();
          loadCounts();
        } catch (e) {
          const err = e.response?.data?.error || 'Could not cancel request';
          message.error(err);
        } finally {
          setCancelingId(null);
        }
      },
    });
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) { message.warning('Please provide a reason'); return; }
    setRejecting(true);
    try {
      await rejectTeleradiologyRequest(rejectModal.requestId, rejectReason.trim());
      message.success('Study returned to coordinator queue');
      setRejectModal({ open: false, requestId: null, patientName: '' });
      setRejectReason('');
      loadWorklist();
    } catch (e) {
      message.error(e.response?.data?.error || 'Could not reject study');
    } finally {
      setRejecting(false);
    }
  };

  const handleComplete = async (requestId) => {
    setClaimingId(requestId);
    try {
      await completeTeleradiologyRequest(requestId);
      message.success('Study marked as completed — report delivered');
      loadWorklist();
      loadHistory(); // refresh history tab count immediately
    } catch (e) {
      const err = e.response?.data?.error || 'Could not mark study as complete';
      message.error(err);
    } finally {
      setClaimingId(null);
    }
  };

  /* ── Not a teleradiology user ── */

  if (!isTeleUser) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#6b7280' }}>
        <GlobalOutlined style={{ fontSize: 48, marginBottom: 16 }} />
        <p style={{ fontSize: 16 }}>This page is only accessible to Afya Telerad platform users.</p>
      </div>
    );
  }

  /* ── Shared column defs ── */

  const baseColumns = [
    {
      title: 'Patient',
      dataIndex: 'patient_name',
      key: 'patient_name',
      render: (v, r) => (
        <div>
          <span style={{ fontWeight: 500 }}>{v || '—'}</span>
          {r.rejection_reason && (
            <Tooltip title={`Rejected by ${r.rejected_by_username || 'radiologist'}: "${r.rejection_reason}"`}>
              <span style={{ marginLeft: 6, fontSize: 11, color: '#d97706', cursor: 'default' }}>
                ↩ Returned
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Modality',
      dataIndex: 'modality',
      key: 'modality',
      width: 80,
      render: (v) => v ? <Tag style={{ fontWeight: 700, fontSize: 12 }}>{v}</Tag> : '—',
    },
    {
      title: 'Urgency',
      dataIndex: 'urgency',
      key: 'urgency',
      width: 100,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        const order = { STAT: 0, URGENT: 1, ROUTINE: 2 };
        return (order[a.urgency] ?? 3) - (order[b.urgency] ?? 3);
      },
      render: (v) => <UrgencyBadge urgency={v} />,
    },
    {
      title: 'Waiting',
      key: 'tat',
      width: 90,
      sorter: (a, b) => new Date(a.submitted_at) - new Date(b.submitted_at),
      render: (_, r) => <TATCell submittedAt={r.submitted_at} urgency={r.urgency} />,
    },
    {
      title: 'Source Hospital',
      dataIndex: 'source_hospital_name',
      key: 'source_hospital_name',
      render: (v, r) => v || r.source_hospital_id || '—',
    },
    {
      title: 'Submitted',
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      width: 130,
      render: (v) => formatTs(v),
    },
  ];

  /* ══════════════════════════════════════════════════════════════════════════
     COORDINATOR VIEW
  ══════════════════════════════════════════════════════════════════════════ */

  if (isCoordinator) {
    const coordinatorColumns = [
      ...baseColumns,
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 130,
        render: (v) => <StatusBadge status={v} />,
      },
      {
        title: 'Clinical Notes',
        dataIndex: 'clinical_notes',
        key: 'clinical_notes',
        ellipsis: true,
        render: (v) => v ? (
          <Tooltip title={v}>
            <span style={{ color: '#6b7280', cursor: 'default' }}>{v}</span>
          </Tooltip>
        ) : <span style={{ color: '#d1d5db' }}>—</span>,
      },
      {
        title: 'Assigned Group',
        key: 'group',
        width: 160,
        render: (_, record) => {
          if (!record.group_id) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
          const g = groups.find(x => x.id === record.group_id);
          const name = g ? g.name.split(' — ')[0] : record.group_id;
          const color = g?.color || '#6b7280';
          return (
            <Tag color={color} style={{ fontSize: 12 }}>{name}</Tag>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 150,
        render: (_, record) => {
          if (record.status === 'completed') {
            return (
              <span style={{ color: '#16a34a', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <CheckOutlined /> Done
              </span>
            );
          }
          if (record.status === 'cancelled') {
            return <span style={{ color: '#9ca3af', fontSize: 13 }}><StopOutlined /> Cancelled</span>;
          }
          const assignLabel = record.status === 'unassigned' ? 'Assign to Group' : 'Reassign Group';
          const menu = (
            <Menu>
              <Menu.Item
                key="assign"
                icon={<UserSwitchOutlined />}
                onClick={() => { setAssignModal({ open: true, request: record }); setSelectedGroup(record.group_id || ''); }}
              >
                {assignLabel}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                key="cancel"
                icon={<StopOutlined />}
                danger
                onClick={() => handleCancel(record.id, record.patient_name)}
              >
                Cancel Request
              </Menu.Item>
            </Menu>
          );
          return (
            <Dropdown overlay={menu} trigger={['click']}>
              <Button size="small" icon={<MoreOutlined />} loading={cancelingId === record.id} />
            </Dropdown>
          );
        },
      },
    ];

    const STATUS_TABS = [
      { key: '',               label: 'All' },
      { key: 'unassigned',     label: `Unassigned (${counts.unassigned})` },
      { key: 'group_assigned', label: `Group Assigned (${counts.group_assigned})` },
      { key: 'claimed',        label: `Claimed (${counts.claimed})` },
      { key: 'completed',      label: `Completed (${counts.completed})` },
    ];

    return (
      <div style={{ padding: '12px 20px', background: '#f8fafc', minHeight: '100vh' }}>

        {/* Compact header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h1 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: '#111827' }}>
            <GlobalOutlined style={{ marginRight: 8, color: '#2a5f5a' }} />
            Teleradiology Dispatch
          </h1>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => { loadQueue(); loadCounts(); }}>Refresh</Button>
        </div>

        {/* Stats cards — full width row above the study list */}
        <Row gutter={12} style={{ marginBottom: 12 }}>
          {[
            { label: 'Unassigned',     value: counts.unassigned,    color: '#dc2626' },
            { label: 'Group Assigned', value: counts.group_assigned, color: '#2563eb' },
            { label: 'Claimed',        value: counts.claimed,       color: '#7c3aed' },
            { label: 'Completed',      value: counts.completed,     color: '#16a34a' },
          ].map(s => (
            <Col key={s.label} span={6}>
              <div style={{
                background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,.04)',
                padding: '16px 20px',
              }}>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontSize: 36, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Status tabs with urgency filter in tab bar */}
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb' }}>
          <Tabs
            activeKey={statusFilter}
            onChange={(k) => { setStatusFilter(k); setQueuePage(1); }}
            style={{ padding: '0 16px' }}
            tabBarStyle={{ marginBottom: 0 }}
            tabBarExtraContent={
              <Select
                placeholder="All urgencies"
                allowClear
                size="small"
                style={{ width: 130, marginBottom: 4 }}
                value={urgencyFilter || undefined}
                onChange={(v) => { setUrgencyFilter(v || ''); setQueuePage(1); }}
              >
                <Option value="STAT"><UrgencyBadge urgency="STAT" /></Option>
                <Option value="URGENT"><UrgencyBadge urgency="URGENT" /></Option>
                <Option value="ROUTINE"><UrgencyBadge urgency="ROUTINE" /></Option>
              </Select>
            }
          >
            {STATUS_TABS.map(t => (
              <TabPane tab={t.label} key={t.key} />
            ))}
          </Tabs>

          <Table
            columns={coordinatorColumns}
            dataSource={queue}
            rowKey="id"
            loading={queueLoading}
            size="small"
            pagination={{
              current: queuePage,
              pageSize: PAGE_SIZE,
              total: queueTotal,
              onChange: (p) => setQueuePage(p),
              showTotal: (total) => `${total} studies`,
              showSizeChanger: false,
              style: { padding: '10px 20px' },
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={statusFilter === '' ? 'No teleradiology requests yet' : `No ${statusFilter.replace('_', ' ')} studies`}
                />
              ),
            }}
            style={{ borderRadius: '0 0 10px 10px', overflow: 'hidden' }}
          />
        </div>

        {/* Assign group modal */}
        <Modal
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserSwitchOutlined style={{ color: '#2a5f5a' }} />
              Assign to Radiologist Group
            </span>
          }
          open={assignModal.open}
          onCancel={() => !assigning && setAssignModal({ open: false, request: null })}
          footer={[
            <Button key="cancel" onClick={() => setAssignModal({ open: false, request: null })} disabled={assigning}>
              Cancel
            </Button>,
            <Button
              key="assign"
              type="primary"
              loading={assigning}
              disabled={!selectedGroup}
              onClick={handleAssignGroup}
              style={{ background: '#2a5f5a', borderColor: '#2a5f5a' }}
            >
              Assign
            </Button>,
          ]}
          width={480}
        >
          {assignModal.request && (
            <div style={{ marginBottom: 20, padding: 12, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {assignModal.request.patient_name || 'Unknown Patient'}
              </div>
              <div style={{ fontSize: 13, color: '#6b7280', display: 'flex', gap: 16 }}>
                <span>{assignModal.request.modality || '—'}</span>
                <span>{assignModal.request.body_part || '—'}</span>
                <UrgencyBadge urgency={assignModal.request.urgency} />
              </div>
              {assignModal.request.clinical_notes && (
                <div style={{ marginTop: 8, fontSize: 13, color: '#374151', fontStyle: 'italic' }}>
                  "{assignModal.request.clinical_notes}"
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 14 }}>Select Group</div>
          <Select
            placeholder="Choose a radiologist group"
            style={{ width: '100%' }}
            value={selectedGroup || undefined}
            onChange={setSelectedGroup}
          >
            {groups.map(g => (
              <Option key={g.id} value={g.id}>{g.name}</Option>
            ))}
          </Select>
        </Modal>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════════════════════
     RADIOLOGIST VIEW
  ══════════════════════════════════════════════════════════════════════════ */

  const groupQueue = worklist.filter(r => r.status === 'group_assigned' && r.group_id === groupId);
  const myStudies  = worklist.filter(r => r.status === 'claimed' && r.assignee_id === userId);

  const radiologistColumns = (isGroupQueue) => [
    ...baseColumns,
    {
      title: 'Report Status',
      key: 'reportStatus',
      width: 140,
      render: (_, record) => {
        if (isGroupQueue) return <StatusBadge status={record.status} />;
        const rs = reportStatusCache[record.study_instance_uid];
        if (rs === 'submitted') {
          return (
            <span className="report-status report-status-submitted">
              <span className="report-status-dot" />
              <span>Completed</span>
              <Tooltip title="Report submitted in viewer">
                <EyeOutlined style={{ marginLeft: 4, cursor: 'default' }} />
              </Tooltip>
            </span>
          );
        }
        if (rs === 'draft') {
          return (
            <span className="report-status report-status-draft">
              <span className="report-status-dot" />
              <span>Draft</span>
            </span>
          );
        }
        return (
          <span className="report-status report-status-none">
            <span className="report-status-dot" />
            <span>No Report</span>
          </span>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: isGroupQueue ? 100 : 190,
      render: (_, record) => isGroupQueue ? (
        <Button
          type="primary"
          size="small"
          icon={<PlayCircleOutlined />}
          loading={claimingId === record.id}
          onClick={() => handleClaim(record.id)}
          style={{ background: '#2a5f5a', borderColor: '#2a5f5a' }}
        >
          Claim
        </Button>
      ) : (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openViewer(record.study_instance_uid)}
            style={{ background: '#1d4ed8', borderColor: '#1d4ed8' }}
          >
            View
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            loading={claimingId === record.id}
            onClick={() => {
              const rs = reportStatusCache[record.study_instance_uid];
              if (rs !== 'submitted') {
                Modal.confirm({
                  title: 'No report found',
                  content: 'No report has been submitted in the viewer for this study. Mark as done anyway?',
                  okText: 'Mark Done',
                  cancelText: 'Cancel',
                  onOk: () => handleComplete(record.id),
                });
              } else {
                handleComplete(record.id);
              }
            }}
            style={{ background: '#16a34a', borderColor: '#16a34a' }}
          >
            Done
          </Button>
          <Tooltip title="Return to group queue (unclaim)">
            <Button
              size="small"
              icon={<UndoOutlined />}
              loading={claimingId === record.id}
              onClick={() => handleUnclaim(record.id)}
            />
          </Tooltip>
          <Tooltip title="Reject — return to coordinator with reason">
            <Button
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => {
                setRejectReason('');
                setRejectModal({ open: true, requestId: record.id, patientName: record.patient_name });
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px 32px', background: '#f8fafc', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#111827' }}>
            <GlobalOutlined style={{ marginRight: 10, color: '#2a5f5a' }} />
            Teleradiology Worklist
          </h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
            Group: {groups.find(g => g.id === groupId)?.name || groupId || '—'}
            {' · '}Logged in as <strong>{username}</strong>
          </p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadWorklist}>Refresh</Button>
      </div>

      {/* Stats */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <StatsCard label="Group Queue" value={groupQueue.length} color="#2563eb" />
        </Col>
        <Col span={6}>
          <StatsCard label="My Claimed" value={myStudies.length} color="#7c3aed" />
        </Col>
        <Col span={6}>
          <StatsCard label="In Worklist" value={worklist.length} color="#374151" />
        </Col>
        <Col span={6}>
          <StatsCard label="My Completed" value={historyTotal} color="#16a34a" />
        </Col>
      </Row>

      {worklistLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Tabs activeKey={worklistTab} onChange={setWorklistTab} tabBarExtraContent={
        <span style={{ fontSize: 12, color: '#9ca3af', paddingRight: 8 }}>
          History refreshes on tab click
        </span>
      }>

          {/* Group queue */}
          <TabPane tab={`Group Queue (${groupQueue.length})`} key="group">
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <div style={{ padding: '12px 20px', borderBottom: '1px solid #f3f4f6', fontSize: 13, color: '#6b7280' }}>
                Studies available for your group — claim one to begin reading
              </div>
              <Table
                columns={radiologistColumns(true)}
                dataSource={groupQueue}
                rowKey="id"
                size="middle"
                pagination={false}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No studies in group queue" /> }}
              />
            </div>
          </TabPane>

          {/* My claimed studies */}
          <TabPane tab={`My Studies (${myStudies.length})`} key="mine">
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <div style={{ padding: '12px 20px', borderBottom: '1px solid #f3f4f6', fontSize: 13, color: '#6b7280' }}>
                Studies you have claimed — open the viewer to read and report
              </div>
              <Table
                columns={radiologistColumns(false)}
                dataSource={myStudies}
                rowKey="id"
                size="middle"
                pagination={false}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No studies claimed yet — claim from the Group Queue" /> }}
              />
            </div>
          </TabPane>

          {/* Reading history — completed studies */}
          <TabPane tab={`My History (${history.length || 0})`} key="history">
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <div style={{ padding: '12px 20px', borderBottom: '1px solid #f3f4f6', fontSize: 13, color: '#6b7280' }}>
                Your completed readings — used for billing and audit
              </div>
              <Table
                columns={[
                  ...baseColumns,
                  {
                    title: 'Completed',
                    dataIndex: 'completed_at',
                    key: 'completed_at',
                    width: 140,
                    render: (v) => formatTs(v),
                  },
                  {
                    title: 'TAT',
                    key: 'tat',
                    width: 90,
                    render: (_, r) => {
                      if (!r.submitted_at || !r.completed_at) return '—';
                      const mins = Math.round((new Date(r.completed_at) - new Date(r.submitted_at)) / 60000);
                      if (mins < 60) return `${mins}m`;
                      return `${Math.floor(mins / 60)}h ${mins % 60}m`;
                    },
                  },
                ]}
                dataSource={history}
                rowKey="id"
                size="middle"
                loading={historyLoading}
                pagination={{ pageSize: 20, showTotal: (t) => `${t} studies` }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No completed studies yet" /> }}
              />
            </div>
          </TabPane>

        </Tabs>
      )}

      {/* Reject modal — shared by both coordinator and radiologist views */}
      <Modal
        title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><StopOutlined style={{ color: '#dc2626' }} /> Reject Study</span>}
        open={rejectModal.open}
        onCancel={() => !rejecting && setRejectModal({ open: false, requestId: null, patientName: '' })}
        footer={[
          <Button key="cancel" onClick={() => setRejectModal({ open: false, requestId: null, patientName: '' })} disabled={rejecting}>Cancel</Button>,
          <Button key="reject" danger loading={rejecting} disabled={!rejectReason.trim()} onClick={handleRejectSubmit}>
            Return to Coordinator
          </Button>,
        ]}
        width={460}
      >
        {rejectModal.patientName && (
          <div style={{ marginBottom: 14, padding: '8px 12px', background: '#fef2f2', borderRadius: 8, fontSize: 13, color: '#374151' }}>
            Study: <strong>{rejectModal.patientName}</strong>
          </div>
        )}
        <div style={{ marginBottom: 6, fontWeight: 500, fontSize: 13 }}>
          Reason for rejection <span style={{ color: '#dc2626' }}>*</span>
        </div>
        <textarea
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
          placeholder="e.g. Outside my subspecialty — please assign to neuroradiology group"
          rows={4}
          maxLength={500}
          style={{
            width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
            borderRadius: 8, fontSize: 13, resize: 'vertical',
            outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
          }}
        />
        <div style={{ textAlign: 'right', fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{rejectReason.length}/500</div>
        <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
          The study returns to the coordinator queue with your reason. The coordinator will review and reassign.
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  profile: state.system.profile,
});

export default connect(mapStateToProps)(withRouter(TeleradiologyPage));
